import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, LayoutDashboard, ChevronDown } from 'lucide-react';

interface Props {
  children: ReactNode;
  /**
   * Optional label shown in the error heading, e.g. "Links Tab".
   * Helps users and developers identify which part of the UI crashed.
   */
  label?: string;
  /**
   * When true the boundary shows a compact inline card instead of a full-page layout.
   * Use this for section-level boundaries inside the dashboard.
   */
  inline?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  showDetails: boolean;
}

/**
 * React class-based ErrorBoundary.
 *
 * Usage:
 *   // Full-page (e.g. around a route):
 *   <ErrorBoundary><Dashboard /></ErrorBoundary>
 *
 *   // Inline section (e.g. around a tab panel):
 *   <ErrorBoundary inline label="Analytics">...</ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, showDetails: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, showDetails: false };
  }

  /** Returns true for chunk-fetch failures caused by a stale deployment. */
  private static isChunkLoadError(error: Error): boolean {
    return (
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Importing a module script failed') ||
      (error as unknown as { code?: string }).code === 'CSS_CHUNK_LOAD_FAILED'
    );
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const label = this.props.label ?? 'unknown section';
    console.error(`[ErrorBoundary] "${label}" crashed:`, error, info.componentStack);

    // Chunk load failures mean the browser has a stale version of the app.
    // Reload once to fetch the new deployment assets.
    if (ErrorBoundary.isChunkLoadError(error)) {
      window.location.reload();
    }
  }

  private reset = () => {
    this.setState({ hasError: false, error: null, showDetails: false });
  };

  private toggleDetails = () => {
    this.setState(s => ({ showDetails: !s.showDetails }));
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { label = 'página', inline } = this.props;
    const { error, showDetails } = this.state;

    if (inline) {
      return (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 space-y-3">
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="text-sm font-medium">
              Erro ao carregar <strong>{label}</strong>
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={this.reset} className="gap-1.5 h-7 text-xs">
              <RefreshCw className="h-3 w-3" />
              Tentar novamente
            </Button>
            <button
              onClick={this.toggleDetails}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Detalhes
              <ChevronDown className={`h-3 w-3 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
            </button>
          </div>
          {showDetails && error && (
            <pre className="text-[10px] bg-muted/60 rounded-md p-3 overflow-x-auto text-muted-foreground whitespace-pre-wrap break-all">
              {error.message}
            </pre>
          )}
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full rounded-2xl border border-border bg-card shadow-lg p-8 space-y-6 text-center">
          <div className="flex justify-center">
            <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-7 w-7 text-destructive" />
            </div>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-foreground">Algo deu errado</h2>
            <p className="text-sm text-muted-foreground">
              Um erro inesperado ocorreu em <strong>{label}</strong>.
              Você pode tentar recarregar a página ou voltar ao dashboard.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              variant="default"
              className="gap-2"
              onClick={() => window.location.reload()}
            >
              <RefreshCw className="h-4 w-4" />
              Recarregar página
            </Button>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => { window.location.href = '/dashboard'; }}
            >
              <LayoutDashboard className="h-4 w-4" />
              Ir para o Dashboard
            </Button>
          </div>

          <button
            onClick={this.toggleDetails}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mx-auto"
          >
            Detalhes técnicos
            <ChevronDown className={`h-3 w-3 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
          </button>

          {showDetails && error && (
            <pre className="text-left text-[10px] bg-muted/60 rounded-md p-3 overflow-x-auto text-muted-foreground whitespace-pre-wrap break-all">
              {error.message}
              {'\n\n'}
              {error.stack}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
